import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Link as LinkIcon, Users, BarChart3, Plus, MessageSquare, Calendar, ChevronRight, Phone, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchSocialLinks, fetchMyProfile } from '../../services/profile';
import { fetchCurrentUser } from '../../services/auth';
import { api } from '../../services/api';
import type { SubscriptionPeriod, SubscriptionPlan, SubscriptionStatus } from '../../services/subscriptions';

interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  period: SubscriptionPeriod | null;
  expiresAt: string | null;
  remainingDays: number | null;
}

const INITIAL_SUBSCRIPTION: SubscriptionInfo = {
  plan: 'free',
  status: 'active',
  period: null,
  expiresAt: null,
  remainingDays: null,
};

// --- CRM Leads helpers ---

interface HomeLead {
  id: number;
  name: string;
  email: string | null;
  mobile_number: string;
  subject: string;
  message: string;
  is_read: number;
  created_at: string;
}

type HomeLeadStatus = 'contacted' | 'follow_up' | 'converted' | 'lost';

const getStoredStatus = (leadId: number): HomeLeadStatus | null => {
  const stored = localStorage.getItem(`lead_status_${leadId}`);
  if (stored === 'contacted' || stored === 'follow_up' || stored === 'converted' || stored === 'lost') {
    return stored;
  }
  return null;
};

const setStoredStatus = (leadId: number, status: HomeLeadStatus | null): void => {
  if (status) {
    localStorage.setItem(`lead_status_${leadId}`, status);
  } else {
    localStorage.removeItem(`lead_status_${leadId}`);
  }
};

const HOME_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-700' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  follow_up: { label: 'Follow-up', color: 'bg-yellow-100 text-yellow-700' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700' },
};

const toWhatsAppUrl = (mobile: string): string => {
  const digits = mobile.replace(/[^\d+]/g, '');
  return `https://wa.me/${digits}`;
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const [socialLinksCount, setSocialLinksCount] = useState<number | null>(null);
  const [collaborationCount, setCollaborationCount] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(INITIAL_SUBSCRIPTION);
  const [recentLeads, setRecentLeads] = useState<HomeLead[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<Record<number, HomeLeadStatus | null>>({});

  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async () => {
      try {
        const [socialRes, profileRes, authRes, leadsRes] = await Promise.allSettled([
          fetchSocialLinks(),
          fetchMyProfile(),
          fetchCurrentUser(),
          api.get('/contact-leads'),
        ]);

        if (!isMounted) return;

        if (socialRes.status === 'fulfilled' && socialRes.value.data.success) {
          const count = socialRes.value.data.count ?? socialRes.value.data.social_links.length;
          setSocialLinksCount(count);
        } else {
          setSocialLinksCount(null);
        }

        if (profileRes.status === 'fulfilled' && profileRes.value.data.success) {
          setCollaborationCount(profileRes.value.data.profile.collaborations?.length ?? 0);
        } else {
          setCollaborationCount(null);
        }

        if (authRes.status === 'fulfilled' && authRes.value.data.success) {
          const authUser = authRes.value.data.user;
          setSubscription({
            plan: authUser.subscription_plan,
            status: authUser.subscription_status,
            period: authUser.subscription_period,
            expiresAt: authUser.subscription_expires_at,
            remainingDays: calculateRemainingDays(authUser.subscription_expires_at),
          });
        } else {
          setSubscription(INITIAL_SUBSCRIPTION);
        }

        if (leadsRes.status === 'fulfilled' && leadsRes.value.data.success) {
          const allLeads: HomeLead[] = leadsRes.value.data.leads || [];
          const topLeads = allLeads.slice(0, 3);
          setRecentLeads(topLeads);
          const statuses: Record<number, HomeLeadStatus | null> = {};
          topLeads.forEach(l => { statuses[l.id] = getStoredStatus(l.id); });
          setLeadStatuses(statuses);
        }
      } catch (error) {
        console.error('[Dashboard] Failed to load data', error);
        if (isMounted) {
          setSocialLinksCount(null);
          setCollaborationCount(null);
          setSubscription(INITIAL_SUBSCRIPTION);
        }
      }
    };

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const updateHomeLeadStatus = (leadId: number, newStatus: HomeLeadStatus) => {
    setStoredStatus(leadId, newStatus);
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    toast.success(`Status updated to ${HOME_STATUS_CONFIG[newStatus]?.label || newStatus}`);
  };

  const isFreePlan = subscription.plan === 'free';
  const expiryLabel = isFreePlan ? 'No expiry' : formatDate(subscription.expiresAt);
  const remainingLabel = isFreePlan
    ? 'Unlimited access'
    : subscription.remainingDays !== null
      ? `${subscription.remainingDays} day${subscription.remainingDays === 1 ? '' : 's'} left`
      : '—';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="bg-gradient-to-r from-[#4f46e5] to-[#6366f1] rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between gap-6">
          <div>
            <p className="text-sm text-primary-100 mb-1">Dashboard</p>
            <h1 className="text-2xl font-bold mb-2 text-white">
              Welcome back, {user?.name ?? 'there'} 👋
            </h1>
            <p className="text-primary-100 text-lg">
              Everything you need to manage your myoneqr presence in one place.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
              <BarChart3 className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </div>

      <section className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            icon={LinkIcon}
            label="Social Links"
            value={formatCount(socialLinksCount)}
            caption="Connected profiles"
            bgColor="bg-blue-50"
            quickAction={{
              label: "Add Link",
              to: "/dashboard/profile"
            }}
          />
          <StatCard
            icon={Users}
            label="Proud Member Of"
            value={formatCount(collaborationCount)}
            caption="Collaborations showcased"
            bgColor="bg-indigo-50"
            quickAction={{
              label: "Add Collaboration",
              to: "/dashboard/profile"
            }}
          />
        </div>

        {/* Recent Leads Table */}
        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Recent Leads</h2>
                <p className="text-sm text-gray-600">Latest contact form submissions</p>
              </div>
            </div>
            <Link
              to="/dashboard/leads"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              View all
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {recentLeads.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No leads yet</p>
            </div>
          ) : (
            <>
              {/* Mobile: stacked cards to prevent overlay/overflow */}
              <div className="md:hidden space-y-3">
                {recentLeads.map((lead) => {
                  const status = leadStatuses[lead.id] ?? null;
                  const statusKey = status || 'new';
                  const cfg = HOME_STATUS_CONFIG[statusKey];
                  return (
                    <div key={lead.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 truncate">{lead.name}</p>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="text-xs text-gray-600 hover:text-primary-600 block truncate">
                              {lead.email}
                            </a>
                          )}
                          <a href={`tel:${lead.mobile_number}`} className="text-xs text-gray-600 hover:text-primary-600">
                            {lead.mobile_number}
                          </a>
                        </div>
                        <select
                          value={status || 'new'}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== 'new') {
                              updateHomeLeadStatus(lead.id, val as HomeLeadStatus);
                            }
                          }}
                          className={`text-xs font-medium px-3 py-1.5 rounded-full border border-transparent cursor-pointer flex-shrink-0 ${cfg.color}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="follow_up">Follow-up</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 truncate mb-3">Subject: {lead.subject}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <div className="flex items-center gap-1">
                          <a href={`tel:${lead.mobile_number}`} className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50" title="Call">
                            <Phone className="h-4 w-4" />
                          </a>
                          <a href={toWhatsAppUrl(lead.mobile_number)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50" title="WhatsApp">
                            <MessageSquare className="h-4 w-4" />
                          </a>
                          <button onClick={() => updateHomeLeadStatus(lead.id, 'follow_up')} className="p-2 rounded-lg text-gray-500 hover:text-yellow-600 hover:bg-yellow-50" title="Follow-up">
                            <Clock className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm table-fixed min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top w-[140px]">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top w-[180px]">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top">Phone</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top w-[120px]">Subject</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top">Actions</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 align-top">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => {
                    const status = leadStatuses[lead.id] ?? null;
                    const statusKey = status || 'new';
                    const cfg = HOME_STATUS_CONFIG[statusKey];
                    return (
                      <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 font-medium text-gray-900 align-top">
                          <span className="block truncate" title={lead.name}>{lead.name}</span>
                        </td>
                        <td className="py-3 px-4 text-gray-600 align-top">
                          {lead.email ? (
                            <a
                              href={`mailto:${lead.email}`}
                              className="block truncate hover:text-primary-600 transition-colors"
                              title={lead.email}
                            >
                              {lead.email}
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600 align-top whitespace-nowrap">
                          <a href={`tel:${lead.mobile_number}`} className="hover:text-primary-600 transition-colors">
                            {lead.mobile_number}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-gray-600 align-top">
                          <span className="block truncate" title={lead.subject}>{lead.subject}</span>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <select
                            value={status || 'new'}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val !== 'new') {
                                updateHomeLeadStatus(lead.id, val as HomeLeadStatus);
                              }
                            }}
                            className={`text-xs font-medium px-3 py-1.5 rounded-full border border-transparent cursor-pointer ${cfg.color}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="follow_up">Follow-up</option>
                            <option value="converted">Converted</option>
                            <option value="lost">Lost</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <div className="flex items-center gap-2">
                            <a
                              href={`tel:${lead.mobile_number}`}
                              className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Call"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                            <a
                              href={toWhatsAppUrl(lead.mobile_number)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                              title="WhatsApp"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </a>
                            <button
                              onClick={() => updateHomeLeadStatus(lead.id, 'follow_up')}
                              className="p-1.5 rounded-lg text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 transition-colors"
                              title="Mark as Follow-up"
                            >
                              <Clock className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-500 align-top whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            {new Date(lead.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Subscription Status</p>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900 capitalize">{subscription.plan}</h2>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${
                  subscription.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : subscription.status === 'expired'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                }`}>
                  {subscription.status}
                </span>
              </div>
              {subscription.period && (
                <p className="text-sm text-gray-600 mt-1">
                  Billing period: {subscription.period}
                </p>
              )}
            </div>
            <Link
              to="/dashboard/account"
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-primary-200 text-primary-600 hover:bg-primary-50 transition-colors text-sm font-medium"
            >
              Manage Subscription
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6 hover:shadow-sm transition text-center">
              <p className="text-sm text-gray-600">Expiry Date</p>
              <p className="text-lg font-semibold text-gray-800 mt-1">{expiryLabel}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6 hover:shadow-sm transition text-center">
              <p className="text-sm text-gray-600">Remaining Days</p>
              <p className="text-lg font-semibold text-gray-800 mt-1">{remainingLabel}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  caption?: string;
  bgColor?: string;
  quickAction?: {
    label: string;
    to: string;
  };
}> = ({ icon: Icon, label, value, caption, bgColor = 'bg-white', quickAction }) => (
  <div className={`${bgColor} rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6`}>
    <div className="flex items-center gap-4 mb-3">
      <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center">
        <Icon className="h-6 w-6 text-gray-700" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-700">{label}</p>
        <p className="text-4xl font-bold text-gray-800">{value}</p>
        {caption && <p className="text-xs text-gray-600 mt-1">{caption}</p>}
      </div>
    </div>
    {quickAction && (
      <Link
        to={quickAction.to}
        className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors mt-2"
      >
        <Plus className="h-3.5 w-3.5" />
        <span>{quickAction.label}</span>
      </Link>
    )}
  </div>
);

const formatCount = (value: number | null) => (value === null ? '—' : value.toString());

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const calculateRemainingDays = (expiresAt: string | null) => {
  if (!expiresAt) return null;
  const end = new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default Home;
